import { Capacitor, registerPlugin } from '@capacitor/core'
import { doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { firebaseApp, firestoreDb } from './firebase'
import { parseChecklistDeepLink } from './checklistDeepLinkService'
import { openWorkspaceTarget, parseWorkspaceDeepLink } from './workspaceSwitch/openWorkspaceTarget'

export type MobilePushRole = 'HELPER' | 'MEMBER' | 'FAMILY'
export type PushPermissionState = 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'

type NativeRegistrationToken = {
  value: string
}

type NativePushNotificationSchema = {
  title?: string
  body?: string
  data?: Record<string, unknown>
}

type NativeActionPerformed = {
  notification: NativePushNotificationSchema
}

type NativePluginListenerHandle = {
  remove: () => Promise<void>
}

type NativePushNotificationsPlugin = {
  requestPermissions: () => Promise<{ receive: PushPermissionState }>
  checkPermissions: () => Promise<{ receive: PushPermissionState }>
  register: () => Promise<void>
  createChannel?: (channel: {
    id: string
    name: string
    description?: string
    importance?: 1 | 2 | 3 | 4 | 5
    visibility?: -1 | 0 | 1
    vibration?: boolean
  }) => Promise<void>
  addListener: {
    (eventName: 'registration', listener: (token: NativeRegistrationToken) => void): Promise<NativePluginListenerHandle>
    (eventName: 'registrationError', listener: (error: unknown) => void): Promise<NativePluginListenerHandle>
    (eventName: 'pushNotificationActionPerformed', listener: (event: NativeActionPerformed) => void): Promise<NativePluginListenerHandle>
  }
}

const PushNotifications = registerPlugin<NativePushNotificationsPlugin>('PushNotifications')

const WEB_SW_PATH = '/firebase-messaging-sw.js'
const SESSION_REGISTERED_KEY = 'timelining.mobilePushRegistered'
const CHANNEL_HIGH = 'timelining_high'
const CHANNEL_DEFAULT = 'timelining_default'

const inFlightByTarget = new Map<string, Promise<void>>()
let nativeListenersReady = false
let nativeRegistrationTarget: { uid: string; role: MobilePushRole } | null = null

const supportsWebPush = (): boolean =>
  typeof window !== 'undefined' && 'serviceWorker' in navigator && 'Notification' in window

const sessionKey = (role: MobilePushRole, uid: string) => `${role}:${uid}`

const sessionAlreadyRegistered = (role: MobilePushRole, uid: string): boolean => {
  if (typeof window === 'undefined') return false
  try {
    return window.sessionStorage.getItem(SESSION_REGISTERED_KEY) === sessionKey(role, uid)
  } catch {
    return false
  }
}

const markSessionRegistered = (role: MobilePushRole, uid: string): void => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(SESSION_REGISTERED_KEY, sessionKey(role, uid))
  } catch {
    // ignore
  }
}

const tokenId = (token: string) => token.replace(/[^A-Za-z0-9._:-]/g, '_').slice(0, 500)

const saveToken = async (
  uid: string,
  role: MobilePushRole,
  token: string,
  platform: 'ANDROID_NATIVE' | 'WEB_PUSH',
) => {
  await setDoc(
    doc(firestoreDb, 'users', uid, 'notification_tokens', tokenId(token)),
    {
      token,
      enabled: true,
      source: role,
      platform,
      provider: 'FCM',
      appSurface: role === 'FAMILY' ? 'FAMILY_WEB' : role === 'HELPER' ? 'HELPER_ANDROID' : 'MEMBER_ANDROID',
      userAgent: typeof navigator === 'undefined' ? null : navigator.userAgent,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  )
}

export const openNativeDeepLink = (notification: NativePushNotificationSchema) => {
  if (typeof window === 'undefined') return
  const data = notification.data ?? {}
  const workspaceId = typeof data.workspaceId === 'string' && data.workspaceId.trim()
    ? data.workspaceId.trim()
    : null

  const deepLink =
    typeof data.workspaceDeepLink === 'string' && data.workspaceDeepLink.startsWith('/')
      ? data.workspaceDeepLink
      : typeof data.deepLink === 'string'
        ? data.deepLink
        : typeof data.deeplink === 'string'
          ? data.deeplink
          : '/'
  if (!deepLink.startsWith('/')) return

  const parsed = parseChecklistDeepLink(deepLink)
  let targetUrl = deepLink
  if (parsed) {
    const qIndex = deepLink.indexOf('?')
    const search = qIndex >= 0 ? deepLink.slice(qIndex) : ''
    targetUrl = window.location.pathname + search
  }

  if (workspaceId) {
    const parsedWsLink = parseWorkspaceDeepLink(deepLink)
    void openWorkspaceTarget({
      workspaceId,
      target: parsedWsLink?.target ?? deepLink,
      source: 'PUSH',
      open: () => {
        window.history.pushState(null, '', targetUrl)
        window.dispatchEvent(new PopStateEvent('popstate'))
      },
    })
    return
  }

  window.history.pushState(null, '', targetUrl)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

const ensureNativeListeners = async (
  uid: string,
  role: MobilePushRole,
  onPermissionChange?: (state: PushPermissionState) => void,
) => {
  nativeRegistrationTarget = { uid, role }
  if (nativeListenersReady) return
  nativeListenersReady = true
  await PushNotifications.addListener('registration', (token) => {
    if (!token.value) return
    const target = nativeRegistrationTarget
    if (!target) return
    void saveToken(target.uid, target.role, token.value, 'ANDROID_NATIVE')
      .then(() => markSessionRegistered(target.role, target.uid))
      .catch((error) => console.warn('[mobile-push] native token save failed', error))
  })
  await PushNotifications.addListener('registrationError', (error) => {
    console.warn('[mobile-push] native registration failed', error)
  })
  await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
    const notifData = event.notification.data ?? {}
    const notifId = typeof notifData.notificationId === 'string' && notifData.notificationId
      ? notifData.notificationId
      : typeof notifData.id === 'string' && notifData.id
        ? notifData.id
        : typeof notifData.eventId === 'string' && notifData.eventId
          ? notifData.eventId
          : null
    if (notifId && nativeRegistrationTarget?.uid) {
      void import('./helper/helperNotificationService').then(({ markHelperNotificationRead }) => {
        if (nativeRegistrationTarget?.uid) {
          void markHelperNotificationRead(nativeRegistrationTarget.uid, notifId).catch((err) => {
            console.warn('[mobile-push] failed to mark notification read on push tap', err)
          })
        }
      })
    }
    openNativeDeepLink(event.notification)
  })
  onPermissionChange?.((await PushNotifications.checkPermissions()).receive)
}

const ensureNativeChannels = async () => {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') return
  if (!PushNotifications.createChannel) return
  await Promise.all([
    PushNotifications.createChannel({
      id: CHANNEL_HIGH,
      name: 'High priority',
      description: 'Mentions, due work, security, and urgent workspace signals.',
      importance: 5,
      visibility: 1,
      vibration: true,
    }).catch(() => undefined),
    PushNotifications.createChannel({
      id: CHANNEL_DEFAULT,
      name: 'General updates',
      description: 'Workspace updates and lower-priority Timelining signals.',
      importance: 4,
      visibility: 1,
      vibration: true,
    }).catch(() => undefined),
  ])
}

const registerNativePush = async (
  uid: string,
  role: MobilePushRole,
  onPermissionChange?: (state: PushPermissionState) => void,
) => {
  await ensureNativeListeners(uid, role, onPermissionChange)
  await ensureNativeChannels()
  const permission = await PushNotifications.requestPermissions()
  onPermissionChange?.(permission.receive)
  if (permission.receive !== 'granted') return
  await PushNotifications.register()
}

const registerWebPush = async (
  uid: string,
  role: MobilePushRole,
  onPermissionChange?: (state: PushPermissionState) => void,
) => {
  if (!supportsWebPush()) return
  const vapidKey = (import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined) ?? ''
  if (!vapidKey) {
    if (import.meta.env.DEV) console.warn('[mobile-push] VITE_FIREBASE_VAPID_KEY missing; web push registration skipped.')
    return
  }
  if (Notification.permission === 'default') {
    const next = await Notification.requestPermission()
    onPermissionChange?.(next === 'granted' ? 'granted' : next === 'denied' ? 'denied' : 'prompt')
  } else {
    onPermissionChange?.(Notification.permission === 'granted' ? 'granted' : Notification.permission === 'denied' ? 'denied' : 'prompt')
  }
  if (Notification.permission !== 'granted') return

  const swRegistration = await navigator.serviceWorker.register(WEB_SW_PATH)
  const { getMessaging, getToken, isSupported } = await import('firebase/messaging')
  if (!(await isSupported())) return
  const messaging = getMessaging(firebaseApp)
  const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swRegistration })
  if (!token) return
  await saveToken(uid, role, token, 'WEB_PUSH')
  markSessionRegistered(role, uid)
}

export const registerMobilePushToken = async (
  uid: string,
  role: MobilePushRole,
  options: { force?: boolean; onPermissionChange?: (state: PushPermissionState) => void } = {},
): Promise<void> => {
  if (!uid) return
  if (!options.force && sessionAlreadyRegistered(role, uid)) return
  const targetKey = sessionKey(role, uid)
  const inFlight = inFlightByTarget.get(targetKey)
  if (inFlight) return inFlight
  const registration = (async () => {
    try {
      if (Capacitor.isNativePlatform()) await registerNativePush(uid, role, options.onPermissionChange)
      else await registerWebPush(uid, role, options.onPermissionChange)
    } catch (error) {
      console.warn('[mobile-push] registration failed', error)
    } finally {
      inFlightByTarget.delete(targetKey)
    }
  })()
  inFlightByTarget.set(targetKey, registration)
  return registration
}

export const getMobilePushPermissionState = async (): Promise<PushPermissionState> => {
  if (Capacitor.isNativePlatform()) {
    try {
      return (await PushNotifications.checkPermissions()).receive
    } catch {
      return 'denied'
    }
  }
  if (!supportsWebPush()) return 'denied'
  return Notification.permission === 'granted' ? 'granted' : Notification.permission === 'denied' ? 'denied' : 'prompt'
}
